/**
 * UsageBillingPage — Usage metrics, cost breakdown, and billing overview.
 * Task 4.12
 */
import { useEffect, useCallback } from "react";
import { Download, BarChart3, DollarSign, Cpu, Crown } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import { Select } from "@/components/ui/Select";
import {
  Table,
  TableHeader,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
} from "@/components/ui/Table";
import { useUsageStore } from "@/stores/useUsageStore";
import { useAgentStore } from "@/stores/useAgentStore";

/** Simple CSS-based bar chart (no chart library) */
function UsageChart({
  data,
}: {
  data: Array<{ date: string; tokens: number; cost: number }>;
}) {
  if (data.length === 0) {
    return (
      <div className="flex h-48 items-center justify-center text-sm text-muted-foreground">
        No usage data for this period
      </div>
    );
  }

  const maxTokens = Math.max(...data.map((d) => d.tokens), 1);

  return (
    <div
      className="flex h-48 items-end gap-1"
      data-testid="usage-chart"
      role="img"
      aria-label="Usage chart"
    >
      {data.map((d) => {
        const height = Math.max((d.tokens / maxTokens) * 100, 2);
        return (
          <div
            key={d.date}
            className="group relative flex flex-1 flex-col items-center"
          >
            <div
              className="w-full rounded-t bg-primary/60 transition-colors group-hover:bg-primary"
              style={{ height: `${height}%` }}
            />
            {/* Tooltip on hover */}
            <div className="pointer-events-none absolute -top-10 left-1/2 -translate-x-1/2 rounded bg-surface-raised px-2 py-1 text-[10px] text-foreground opacity-0 shadow transition-opacity group-hover:opacity-100">
              {d.tokens.toLocaleString()} tokens
            </div>
            <span className="mt-1 text-[9px] text-muted">
              {new Date(d.date).getDate()}
            </span>
          </div>
        );
      })}
    </div>
  );
}

const dateRangePresets = [
  { label: "Last 7 days", days: 7 },
  { label: "Last 30 days", days: 30 },
  { label: "Last 90 days", days: 90 },
];

export function UsageBillingPage() {
  const {
    summary,
    events,
    dateRange,
    isLoading,
    loadUsage,
    loadSummary,
    exportCsv,
    setDateRange,
  } = useUsageStore();
  const { agents, loadAgents } = useAgentStore();

  useEffect(() => {
    loadUsage();
    loadSummary();
    if (agents.length === 0) loadAgents();
  }, [loadUsage, loadSummary, agents.length, loadAgents]);

  const handleDateChange = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const days = parseInt(e.target.value, 10);
      const end = new Date().toISOString().split("T")[0];
      const start = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
        .toISOString()
        .split("T")[0];
      const range = { start, end };
      setDateRange(range);
      loadUsage(range);
    },
    [setDateRange, loadUsage],
  );

  const handleExport = useCallback(async () => {
    const blob = await exportCsv();
    if (blob) {
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `usage-${dateRange.start}-${dateRange.end}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    }
  }, [exportCsv, dateRange]);

  // Build agent breakdown from events
  const agentBreakdown = (() => {
    const map = new Map<string, { tokens: number; cost: number; messages: number }>();
    for (const ev of events ?? []) {
      const existing = map.get(ev.agentId) ?? { tokens: 0, cost: 0, messages: 0 };
      existing.tokens += ev.tokens;
      existing.cost += ev.cost;
      existing.messages += 1;
      map.set(ev.agentId, existing);
    }
    const totalTokens = summary?.totalTokens ?? 1;
    return Array.from(map.entries())
      .map(([agentId, data]) => {
        const agent = agents.find((a) => a.id === agentId);
        return {
          agentId,
          name: agent?.name ?? agentId,
          ...data,
          pct: totalTokens > 0 ? ((data.tokens / totalTokens) * 100).toFixed(1) : "0",
        };
      })
      .sort((a, b) => b.tokens - a.tokens);
  })();

  return (
    <div className="flex h-full flex-col" data-testid="usage-billing-page">
      {/* Header */}
      <div className="border-b border-white/10 px-6 py-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white">Usage & Billing</h1>
            <p className="mt-1 text-sm text-muted-foreground">
              Monitor your usage and manage billing
            </p>
          </div>
          <div className="flex items-center gap-3">
            <div className="w-44">
              <Select onChange={handleDateChange} aria-label="Date range">
                {dateRangePresets.map((p) => (
                  <option key={p.days} value={p.days}>
                    {p.label}
                  </option>
                ))}
              </Select>
            </div>
            <Button variant="secondary" size="sm" onClick={handleExport}>
              <Download className="h-4 w-4" />
              Export CSV
            </Button>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-6">
        {/* Summary metrics */}
        <div className="mb-6 grid gap-4 sm:grid-cols-3" data-testid="summary-metrics">
          <div className="rounded-xl border border-white/10 bg-surface-raised p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary">
                <BarChart3 className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted">Total Tokens</p>
                <p className="text-xl font-bold text-white">
                  {summary?.totalTokens?.toLocaleString() ?? "--"}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-surface-raised p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-sage/10 text-accent-sage">
                <DollarSign className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted">Total Cost</p>
                <p className="text-xl font-bold text-white">
                  ${summary?.totalCost?.toFixed(2) ?? "--"}
                </p>
              </div>
            </div>
          </div>
          <div className="rounded-xl border border-white/10 bg-surface-raised p-5">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-lavender/10 text-accent-lavender">
                <Cpu className="h-5 w-5" />
              </div>
              <div>
                <p className="text-xs text-muted">Active Agents</p>
                <p className="text-xl font-bold text-white">
                  {agents.length}
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Usage chart */}
        <div className="mb-6 rounded-xl border border-white/10 bg-surface-raised p-5">
          <h2 className="mb-4 text-sm font-semibold text-white">
            Tokens Per Day
          </h2>
          {isLoading ? (
            <div className="flex h-48 items-center justify-center">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            </div>
          ) : (
            <UsageChart data={summary?.byDay ?? []} />
          )}
        </div>

        {/* Breakdown table */}
        <div className="mb-6 rounded-xl border border-white/10 bg-surface-raised p-5">
          <h2 className="mb-4 text-sm font-semibold text-white">
            Usage by Agent
          </h2>
          {agentBreakdown.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              No usage data available
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Agent</TableHead>
                  <TableHead>Tokens</TableHead>
                  <TableHead>Cost</TableHead>
                  <TableHead>Messages</TableHead>
                  <TableHead>% of Total</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {agentBreakdown.map((row) => (
                  <TableRow key={row.agentId}>
                    <TableCell className="font-medium">{row.name}</TableCell>
                    <TableCell>{row.tokens.toLocaleString()}</TableCell>
                    <TableCell>${row.cost.toFixed(2)}</TableCell>
                    <TableCell>{row.messages}</TableCell>
                    <TableCell>{row.pct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        {/* Billing section (placeholder for Phase 5) */}
        <div className="rounded-xl border border-white/10 bg-surface-raised p-5">
          <h2 className="mb-4 flex items-center gap-2 text-sm font-semibold text-white">
            <Crown className="h-4 w-4 text-accent-blush" />
            Billing
          </h2>
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-center gap-2">
                <Badge variant="primary">Free Plan</Badge>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Upgrade to Pro for higher limits and priority support
              </p>
            </div>
            <Button variant="secondary" disabled>
              Upgrade (Coming Soon)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
